import type { SxProps, Theme } from '@mui/material'
import { Box, ButtonBase, Typography } from '@mui/material'
import { Brain, Menu } from '@tunasong/icons'
import { isAIGenerated } from '@tunasong/models'
import type { TunaEditor } from '@tunasong/plugin-lib'
import { useEditorMessage } from '@tunasong/plugin-lib'
import type { CoreElement } from '@tunasong/schemas'
import React, { useCallback, useState } from 'react'
import type { FC } from 'react'
import type { DragElementWrapper, DragSourceOptions } from 'react-dnd'
import { useReadOnly } from 'slate-react'
import { BlockMenu } from './block-menu.js'
import { BlockToolbar } from './block-toolbar.js'

const gutterSx: SxProps<Theme> = {
  position: 'absolute',
  top: 0,
  left: theme => theme.spacing(-2),
  transform: 'translateX(-100%)',
  display: 'flex',
  height: '100%',
  justifyContent: 'center',
  width: theme => theme.spacing(2.5),
  transition: 'opacity 0.2s',
  borderRadius: 0,
}

const menuButtonSx: SxProps<Theme> = {
  top: theme => theme.spacing(-1),
  minWidth: 18,
  height: 18,
  padding: 0,
  backgroundColor: 'transparent',
  backgroundRepeat: 'no-repeat',
  border: 'none',
  cursor: 'pointer',
  overflow: 'hidden',
  outline: 'none',
  opacity: 0.3,
  zIndex: 1,
  ':hover': {
    opacity: 1,
  },
}

const menuIconSx: SxProps<Theme> = {
  fontSize: 24,
  position: 'absolute',
  color: theme => theme.vars.palette.text.secondary,
}

const aiBoxSx: SxProps<Theme> = {
  position: 'absolute',
  bottom: 0,
  left: theme => theme.spacing(0.7),
}

const aiIconSx: SxProps<Theme> = {
  color: theme => theme.vars.palette.secondary.light,
}

interface BlockGutterProps {
  editor: TunaEditor
  element: CoreElement
  blockName: string
  hasFocus: boolean
  label?: string
  dragRef?: DragElementWrapper<DragSourceOptions>
}

export const BlockGutter: FC<BlockGutterProps> = ({ editor, element, blockName, hasFocus, label, dragRef }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [clickedAway, setClickedAway] = useState(0)
  const readOnly = useReadOnly()

  const showProperties = useEditorMessage({ editor, type: 'show-block-properties' })
  const handleShowProperties = useCallback(() => showProperties(element, anchorEl), [anchorEl, element, showProperties])

  const showComments = useEditorMessage({ editor, type: 'show-comments' })
  const handleShowComments = useCallback(() => showComments(element, anchorEl), [anchorEl, element, showComments])

  const handleMenu = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      if (anchorEl || Date.now() < clickedAway + 1000) {
        setAnchorEl(null)
        setClickedAway(0)
      } else {
        setAnchorEl(event.currentTarget)
      }
    },
    [anchorEl, clickedAway]
  )

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null)
    setClickedAway(Date.now())
  }, [])

  const handleMouseDown = useCallback(
    (e: React.SyntheticEvent) => {
      e.stopPropagation()
      editor.selection = null
    },
    [editor]
  )

  return (
    <>
      <Box tabIndex={-1} title={blockName} contentEditable={false} aria-label="Gutter left" sx={gutterSx}>
        {!readOnly ? (
          <BlockToolbar tabIndex={-1} ref={dragRef as never}>
            <ButtonBase
              sx={menuButtonSx}
              onClick={handleMenu}
              onMouseDown={handleMouseDown}
              aria-label="Block menu"
              contentEditable={false}
            >
              <Menu sx={menuIconSx} fontSize="inherit" />
            </ButtonBase>
          </BlockToolbar>
        ) : null}
        {isAIGenerated(element) ? (
          <Box title="Assisted by Tunabrain creative AI" sx={aiBoxSx}>
            <Brain sx={aiIconSx} />
          </Box>
        ) : null}
        {label ? (
          <Typography
            variant="body2"
            contentEditable={false}
            sx={{
              left: theme => theme.spacing(1),
              textTransform: 'uppercase',
              color: theme => (hasFocus ? theme.vars.palette.primary.main : theme.vars.palette.text.secondary),
              writingMode: 'vertical-rl',
              textAlign: 'center',
              position: 'relative',
              transform: 'rotate(180deg)',
              whiteSpace: 'nowrap',
              userSelect: 'none',
              height: '100%',
            }}
          >
            {label}
          </Typography>
        ) : null}
      </Box>
      {anchorEl ? (
        <BlockMenu
          open={true}
          anchorEl={anchorEl}
          block={element}
          onShowProperties={handleShowProperties}
          onComments={handleShowComments}
          onClose={handleCloseMenu}
        />
      ) : null}
    </>
  )
}
